import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import API from "./config/API";

function ConfigurationSetupCount() {
  const [data, setData] = useState(null);
  const [message, setMessage] = useState("");
  const [messageColor, setMessageColor] = useState("green");
  const [bedSearch, setBedSearch] = useState("");

  const navigate = useNavigate();

  // =========================================================
  // USER DETAILS
  // =========================================================

  const loggedUser = JSON.parse(localStorage.getItem("user"));
  const emailId = loggedUser?.emailId || "";

  const roleType =
    localStorage.getItem("roleType") || loggedUser?.roleType || "";

  const allowedRoles = ["APP_ADMIN", "APP_EXECUTOR"];

  const canNavigate = allowedRoles.includes(roleType.trim().toUpperCase());

  // =========================================================
  // DATA
  // =========================================================

  const rooms = data?.["Total Rooms Count "] || [];
  const beds = data?.["Total Beds Count "] || [];

  // =========================================================
  // FLOOR COLORS
  // =========================================================

  const floorColors = [
    "#eff6ff",
    "#ecfdf5",
    "#fffbeb",
    "#fdf2f8",
    "#f5f3ff",
    "#ecfeff",
  ];

  const getFloorColor = (floorName) => {
    const uniqueFloors = [...new Set(rooms.map((item) => item.FloorName))];

    const floorIndex = uniqueFloors.indexOf(floorName);

    return floorColors[floorIndex >= 0 ? floorIndex % floorColors.length : 0];
  };

  // =========================================================
  // FETCH DATA
  // =========================================================

  useEffect(() => {
    if (emailId) {
      fetchData();
    }
  }, [emailId]);

  const fetchData = async () => {
    try {
      const res = await axios.get(
        `${API.V1}/maxLimit/getConfigurationFloorsRoomsBedsCount`,
        {
          params: {
            emailId: emailId,
          },
          headers: {
            Authorization: `Bearer ${loggedUser?.jwtToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      const response = res.data;

      if (response.statusCode === 200) {
        setData(response.data);
        setMessage(response.message);
        setMessageColor("green");
      } else if (response.statusCode === 400) {
        setMessage(response.message);
        setMessageColor("orange");
      } else {
        setMessage(response.message || "Something went wrong");
        setMessageColor("red");
      }
    } catch (err) {
      console.error("Configuration setup error:", err);

      setMessage("Server down, please try after sometime !");
      setMessageColor("red");
    }
  };

  // =========================================================
  // FILTER BEDS
  // =========================================================

  const filteredBeds = beds.filter((item) =>
    item.roomName?.toLowerCase().includes(bedSearch.toLowerCase()),
  );

  // =========================================================
  // MESSAGE COLORS
  // =========================================================

  const getMessageBackground = () => {
    if (messageColor === "green") {
      return "#ecfdf5";
    }

    if (messageColor === "orange") {
      return "#fffbeb";
    }

    return "#fef2f2";
  };

  const getMessageTextColor = () => {
    if (messageColor === "green") {
      return "#047857";
    }

    if (messageColor === "orange") {
      return "#b45309";
    }

    return "#b91c1c";
  };

  // =========================================================
  // NAVIGATION
  // =========================================================

  const openFloorConfiguration = () => {
    if (!canNavigate) return;

    navigate("/maxFloorCountForm", {
      state: {
        floorCount: data?.["Total floors Count"] || 0,
      },
    });
  };

  const openRoomConfiguration = () => {
    if (!canNavigate) return;

    navigate("/roomMaxSetUp");
  };

  const openBedConfiguration = () => {
    if (!canNavigate) return;

    navigate("/bedMaxCountForm");
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div style={styles.container}>
      {/* =====================================================
          SIDEBAR
      ===================================================== */}

      <aside style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <div style={styles.logoIcon}>PG</div>

          <div>
            <div style={styles.logoTitle}>PG Configuration</div>

            <div style={styles.logoSubtitle}>Setup & Management</div>
          </div>
        </div>

        <div style={styles.menuLabel}>CONFIGURATION</div>

        <Link to="/dashboard" style={styles.activeLink}>
          <span style={styles.linkIcon}>⌂</span>
          Dashboard
        </Link>

        <Link
          to={canNavigate ? "/maxFloorCountForm" : "#"}
          state={{
            maxFloorCount: data?.["Total floors Count"] ?? 0,
          }}
          style={{
            ...styles.link,
            cursor: canNavigate ? "pointer" : "not-allowed",
            opacity: canNavigate ? 1 : 0.55,
          }}
          onClick={(e) => {
            if (!canNavigate) {
              e.preventDefault();
            }
          }}
        >
          <span style={styles.linkIcon}>▦</span>
          Floors
          {!canNavigate && <span style={styles.lockIcon}>🔒</span>}
        </Link>

        <Link to="/roomMaxSetUp" style={styles.link}>
          <span style={styles.linkIcon}>⌂</span>
          Rooms
        </Link>

        <Link to="/bedMaxCountForm" style={styles.link}>
          <span style={styles.linkIcon}>▣</span>
          Beds
        </Link>

        <div style={styles.sidebarBottom}>
          <div style={styles.userCard}>
            <div style={styles.userAvatar}>
              {(loggedUser?.userName || "U").charAt(0).toUpperCase()}
            </div>

            <div style={styles.userInfo}>
              <div style={styles.userName}>
                {loggedUser?.userName || "User"}
              </div>

              <div style={styles.userRole}>{roleType || "USER"}</div>
            </div>
          </div>
        </div>
      </aside>

      {/* =====================================================
          MAIN CONTENT
      ===================================================== */}

      <main style={styles.main}>
        {/* PAGE HEADER */}

        <div style={styles.pageHeader}>
          <div>
            <div style={styles.breadcrumb}>Dashboard / Configuration</div>

            <h1 style={styles.heading}>Configuration Setup</h1>

            <p style={styles.subHeading}>
              Manage maximum floors, rooms and beds configured for your PG.
            </p>
          </div>

          <div style={styles.headerBadge}>
            <span style={styles.statusDot}></span>
            Configuration Active
          </div>
        </div>

        {/* MESSAGE */}

        {message && (
          <div
            style={{
              ...styles.messageBox,
              background: getMessageBackground(),
              color: getMessageTextColor(),
              borderColor:
                messageColor === "green"
                  ? "#a7f3d0"
                  : messageColor === "orange"
                    ? "#fde68a"
                    : "#fecaca",
            }}
          >
            <span style={styles.messageIcon}>
              {messageColor === "green"
                ? "✓"
                : messageColor === "orange"
                  ? "!"
                  : "×"}
            </span>

            <span>{message}</span>
          </div>
        )}

        {/* =====================================================
            EMPTY STATE
        ===================================================== */}

        {!data ? (
          <div style={styles.emptyBox}>
            <div style={styles.emptyIcon}>⟳</div>

            <div style={styles.emptyTitle}>No Configuration Data</div>

            <div style={styles.emptyText}>
              Configuration information is not available at the moment.
            </div>
          </div>
        ) : (
          <>
            {/* =================================================
                SUMMARY CARDS
            ================================================= */}

            <div style={styles.summaryGrid}>
              {/* FLOORS */}

              <div
                style={{
                  ...styles.summaryCard,
                  borderTop: "3px solid #2563eb",
                  cursor: canNavigate ? "pointer" : "default",
                }}
                onClick={openFloorConfiguration}
              >
                <div style={styles.summaryTop}>
                  <div
                    style={{
                      ...styles.summaryIcon,
                      background: "#eff6ff",
                      color: "#2563eb",
                    }}
                  >
                    ▦
                  </div>

                  <span style={styles.summaryArrow}>→</span>
                </div>

                <div style={styles.summaryLabel}>TOTAL FLOORS</div>

                <div
                  style={{
                    ...styles.summaryValue,
                    color: "#1d4ed8",
                  }}
                >
                  {data["Total floors Count"] || 0}
                </div>

                <div style={styles.summaryFooter}>Configured floors</div>
              </div>

              {/* ROOMS */}

              <div
                style={{
                  ...styles.summaryCard,
                  borderTop: "3px solid #059669",
                  cursor: canNavigate ? "pointer" : "default",
                }}
                onClick={openRoomConfiguration}
              >
                <div style={styles.summaryTop}>
                  <div
                    style={{
                      ...styles.summaryIcon,
                      background: "#ecfdf5",
                      color: "#059669",
                    }}
                  >
                    ⌂
                  </div>

                  <span style={styles.summaryArrow}>→</span>
                </div>

                <div style={styles.summaryLabel}>TOTAL ROOMS</div>

                <div
                  style={{
                    ...styles.summaryValue,
                    color: "#047857",
                  }}
                >
                  {rooms.reduce(
                    (total, item) => total + Number(item.roomsCount || 0),
                    0,
                  )}
                </div>

                <div style={styles.summaryFooter}>Configured rooms</div>
              </div>

              {/* BEDS */}

              <div
                style={{
                  ...styles.summaryCard,
                  borderTop: "3px solid #7c3aed",
                  cursor: canNavigate ? "pointer" : "default",
                }}
                onClick={openBedConfiguration}
              >
                <div style={styles.summaryTop}>
                  <div
                    style={{
                      ...styles.summaryIcon,
                      background: "#f5f3ff",
                      color: "#7c3aed",
                    }}
                  >
                    ▣
                  </div>

                  <span style={styles.summaryArrow}>→</span>
                </div>

                <div style={styles.summaryLabel}>TOTAL BEDS</div>

                <div
                  style={{
                    ...styles.summaryValue,
                    color: "#6d28d9",
                  }}
                >
                  {beds.reduce(
                    (total, item) => total + Number(item.maxbedsCount || 0),
                    0,
                  )}
                </div>

                <div style={styles.summaryFooter}>Configured beds</div>
              </div>
            </div>

            {/* =================================================
                DETAILS GRID
            ================================================= */}

            <div style={styles.grid}>
              {/* =================================================
                  FLOORS CARD
              ================================================= */}

              <div
                style={{
                  ...styles.card,
                  cursor: canNavigate ? "pointer" : "default",
                }}
                onClick={openFloorConfiguration}
              >
                <div style={styles.cardHeader}>
                  <div style={styles.cardHeaderLeft}>
                    <div
                      style={{
                        ...styles.cardHeaderIcon,
                        background: "#eff6ff",
                        color: "#2563eb",
                      }}
                    >
                      ▦
                    </div>

                    <div>
                      <h3 style={styles.cardTitle}>Floors Summary</h3>

                      <p style={styles.cardSubtitle}>Floor configuration</p>
                    </div>
                  </div>

                  {!canNavigate && (
                    <span style={styles.lockBadge}>🔒 Restricted</span>
                  )}
                </div>

                <div style={styles.countBox}>
                  <div>
                    <div style={styles.countLabel}>Total Floors</div>

                    <div style={styles.countDescription}>
                      Maximum configured floors
                    </div>
                  </div>

                  <div
                    style={{
                      ...styles.countNumber,
                      color: "#2563eb",
                    }}
                  >
                    {data["Total floors Count"] || 0}
                  </div>
                </div>
              </div>

              {/* =================================================
                  ROOMS CARD
              ================================================= */}

              <div
                style={{
                  ...styles.card,
                  cursor: canNavigate ? "pointer" : "default",
                }}
                onClick={openRoomConfiguration}
              >
                <div style={styles.cardHeader}>
                  <div style={styles.cardHeaderLeft}>
                    <div
                      style={{
                        ...styles.cardHeaderIcon,
                        background: "#ecfdf5",
                        color: "#059669",
                      }}
                    >
                      ⌂
                    </div>

                    <div>
                      <h3 style={styles.cardTitle}>Rooms Count</h3>

                      <p style={styles.cardSubtitle}>Rooms by floor</p>
                    </div>
                  </div>
                </div>

                <div style={styles.tableHeader}>
                  <span>Floor Name</span>
                  <span>Rooms</span>
                </div>

                <div style={styles.scroll}>
                  {rooms.length > 0 ? (
                    rooms.map((item, i) => (
                      <div key={i} style={styles.row}>
                        <span
                          style={{
                            ...styles.floorText,
                            color: "#2563eb",
                          }}
                        >
                          {item.FloorName}
                        </span>

                        <span
                          style={{
                            ...styles.numberBadge,
                            background: "#ecfdf5",
                            color: "#047857",
                          }}
                        >
                          {item.roomsCount}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div style={styles.noData}>No room configuration found</div>
                  )}
                </div>
              </div>

              {/* =================================================
                  BEDS CARD
              ================================================= */}

              <div
                style={{
                  ...styles.card,
                  cursor: canNavigate ? "pointer" : "default",
                }}
                onClick={openBedConfiguration}
              >
                <div style={styles.cardHeader}>
                  <div style={styles.cardHeaderLeft}>
                    <div
                      style={{
                        ...styles.cardHeaderIcon,
                        background: "#f5f3ff",
                        color: "#7c3aed",
                      }}
                    >
                      ▣
                    </div>

                    <div>
                      <h3 style={styles.cardTitle}>Beds Count</h3>

                      <p style={styles.cardSubtitle}>Beds by room</p>
                    </div>
                  </div>
                </div>

                {/* SEARCH */}

                <div style={styles.searchWrapper}>
                  <span style={styles.searchIcon}>⌕</span>

                  <input
                    type="text"
                    placeholder="Search room name..."
                    value={bedSearch}
                    onChange={(e) => setBedSearch(e.target.value)}
                    style={styles.searchInput}
                    onClick={(e) => e.stopPropagation()}
                  />

                  {bedSearch && (
                    <button
                      type="button"
                      style={styles.clearButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        setBedSearch("");
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>

                <div style={styles.tableHeader}>
                  <span>Room Name</span>
                  <span>Beds</span>
                </div>

                <div style={styles.scroll}>
                  {filteredBeds.length > 0 ? (
                    filteredBeds.map((item, i) => (
                      <div
                        key={i}
                        style={{
                          ...styles.row,
                          background: getFloorColor(item.floorName),
                        }}
                      >
                        <span
                          style={{
                            ...styles.floorText,
                            color: "#7c3aed",
                          }}
                        >
                          {item.floorName}-{item.roomName}
                        </span>

                        <span
                          style={{
                            ...styles.numberBadge,
                            background: "#fef2f2",
                            color: "#dc2626",
                          }}
                        >
                          {item.maxbedsCount}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div style={styles.noData}>No room found</div>
                  )}
                </div>
              </div>
            </div>

            {/* =================================================
                CREATE FLOOR DASHBOARD BUTTON
            ================================================= */}

            <div style={styles.bottomAction}>
              <button
                type="button"
                style={styles.createFloorButton}
                onClick={() => navigate("/floorRoomsBeds")}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 18px rgba(30, 58, 138, 0.25)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 5px 14px rgba(30, 58, 138, 0.18)";
                }}
              >
                <span>Create Floor Dashboard</span>

                <span style={styles.buttonArrow}>→</span>
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default ConfigurationSetupCount;

/* ============================================================
   MODERN STYLES
============================================================ */

const styles = {
  // ==========================================================
  // MAIN CONTAINER
  // ==========================================================

  container: {
    display: "flex",
    minHeight: "100vh",
    background: "#f8fafc",
    fontFamily: "Inter, Arial, Helvetica, sans-serif",
    color: "#1e293b",
  },

  // ==========================================================
  // SIDEBAR
  // ==========================================================

  sidebar: {
    width: "245px",
    minWidth: "245px",
    minHeight: "100vh",
    background: "linear-gradient(180deg, #0f172a 0%, #111827 100%)",
    color: "#fff",
    padding: "20px 15px",
    display: "flex",
    flexDirection: "column",
    boxShadow: "4px 0 18px rgba(15, 23, 42, 0.10)",
  },

  sidebarHeader: {
    display: "flex",
    alignItems: "center",
    gap: "11px",
    padding: "4px 6px 22px",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  },

  logoIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "13px",
    fontWeight: "800",
    color: "#fff",
    boxShadow: "0 5px 14px rgba(37,99,235,0.25)",
  },

  logoTitle: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#f8fafc",
  },

  logoSubtitle: {
    marginTop: "3px",
    fontSize: "10px",
    color: "#94a3b8",
  },

  menuLabel: {
    marginTop: "24px",
    marginBottom: "8px",
    paddingLeft: "8px",
    fontSize: "9px",
    fontWeight: "700",
    color: "#64748b",
    letterSpacing: "1px",
  },

  link: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 11px",
    marginBottom: "6px",
    color: "#cbd5e1",
    textDecoration: "none",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: "600",
    transition: "all 0.2s ease",
  },

  activeLink: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 11px",
    marginBottom: "6px",
    color: "#fff",
    textDecoration: "none",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: "600",
    background: "linear-gradient(90deg, #1e40af, #1d4ed8)",
    boxShadow: "0 4px 12px rgba(30,64,175,0.25)",
  },

  linkIcon: {
    width: "23px",
    height: "23px",
    borderRadius: "6px",
    background: "rgba(255,255,255,0.07)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
  },

  lockIcon: {
    marginLeft: "auto",
    fontSize: "10px",
  },

  sidebarBottom: {
    marginTop: "auto",
    paddingTop: "15px",
  },

  userCard: {
    display: "flex",
    alignItems: "center",
    gap: "9px",
    padding: "10px",
    borderRadius: "9px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.06)",
  },

  userAvatar: {
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    background: "#2563eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: "12px",
    fontWeight: "700",
  },

  userInfo: {
    minWidth: 0,
  },

  userName: {
    fontSize: "11px",
    fontWeight: "700",
    color: "#e2e8f0",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "150px",
  },

  userRole: {
    marginTop: "2px",
    fontSize: "9px",
    color: "#64748b",
  },

  // ==========================================================
  // MAIN
  // ==========================================================

  main: {
    flex: 1,
    minWidth: 0,
    padding: "24px 26px",
    overflow: "auto",
  },

  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "20px",
    marginBottom: "20px",
  },

  breadcrumb: {
    fontSize: "10px",
    color: "#94a3b8",
    marginBottom: "6px",
  },

  heading: {
    margin: 0,
    fontSize: "23px",
    fontWeight: "750",
    color: "#0f172a",
    letterSpacing: "-0.4px",
  },

  subHeading: {
    margin: "5px 0 0",
    fontSize: "12px",
    color: "#64748b",
  },

  headerBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    padding: "7px 11px",
    borderRadius: "20px",
    background: "#ecfdf5",
    color: "#047857",
    fontSize: "10px",
    fontWeight: "700",
    whiteSpace: "nowrap",
  },

  statusDot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    background: "#10b981",
  },

  // ==========================================================
  // MESSAGE
  // ==========================================================

  messageBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "10px 14px",
    borderRadius: "8px",
    border: "1px solid",
    marginBottom: "18px",
    fontSize: "11px",
    fontWeight: "600",
  },

  messageIcon: {
    width: "19px",
    height: "19px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "800",
  },

  // ==========================================================
  // SUMMARY
  // ==========================================================

  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "14px",
    marginBottom: "18px",
  },

  summaryCard: {
    background: "#fff",
    borderRadius: "11px",
    padding: "15px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 3px 10px rgba(15,23,42,0.045)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
  },

  summaryTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px",
  },

  summaryIcon: {
    width: "34px",
    height: "34px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "15px",
    fontWeight: "700",
  },

  summaryArrow: {
    color: "#94a3b8",
    fontSize: "16px",
  },

  summaryLabel: {
    fontSize: "9px",
    fontWeight: "800",
    color: "#94a3b8",
    letterSpacing: "0.6px",
  },

  summaryValue: {
    marginTop: "5px",
    fontSize: "25px",
    lineHeight: "1",
    fontWeight: "800",
  },

  summaryFooter: {
    marginTop: "7px",
    fontSize: "10px",
    color: "#94a3b8",
  },

  // ==========================================================
  // GRID
  // ==========================================================

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "16px",
    alignItems: "start",
  },

  // ==========================================================
  // CARD
  // ==========================================================

  card: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "11px",
    padding: "15px",
    minWidth: 0,
    boxShadow: "0 3px 10px rgba(15,23,42,0.045)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
  },

  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    marginBottom: "14px",
  },

  cardHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  cardHeaderIcon: {
    width: "34px",
    height: "34px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "15px",
    fontWeight: "700",
  },

  cardTitle: {
    margin: 0,
    fontSize: "13px",
    fontWeight: "750",
    color: "#1e293b",
  },

  cardSubtitle: {
    margin: "3px 0 0",
    fontSize: "10px",
    color: "#94a3b8",
  },

  lockBadge: {
    padding: "4px 7px",
    borderRadius: "5px",
    background: "#fef2f2",
    color: "#b91c1c",
    fontSize: "8px",
    fontWeight: "700",
  },

  // ==========================================================
  // COUNT BOX
  // ==========================================================

  countBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px",
    borderRadius: "9px",
    background: "#f8fafc",
    border: "1px solid #f1f5f9",
  },

  countLabel: {
    fontSize: "11px",
    fontWeight: "700",
    color: "#334155",
  },

  countDescription: {
    marginTop: "3px",
    fontSize: "9px",
    color: "#94a3b8",
  },

  countNumber: {
    fontSize: "25px",
    fontWeight: "800",
  },

  // ==========================================================
  // TABLE
  // ==========================================================

  tableHeader: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 9px",
    marginBottom: "5px",
    color: "#94a3b8",
    fontSize: "9px",
    fontWeight: "800",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "1px solid #f1f5f9",
  },

  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    padding: "9px 10px",
    marginBottom: "6px",
    borderRadius: "7px",
    border: "1px solid rgba(226,232,240,0.7)",
  },

  floorText: {
    fontSize: "11px",
    fontWeight: "700",
  },

  numberBadge: {
    minWidth: "28px",
    padding: "4px 7px",
    borderRadius: "5px",
    textAlign: "center",
    fontSize: "10px",
    fontWeight: "800",
  },

  scroll: {
    maxHeight: "285px",
    overflowY: "auto",
    paddingRight: "3px",
  },

  // ==========================================================
  // SEARCH
  // ==========================================================

  searchWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    marginBottom: "10px",
  },

  searchIcon: {
    position: "absolute",
    left: "10px",
    color: "#94a3b8",
    fontSize: "14px",
    zIndex: 1,
  },

  searchInput: {
    width: "100%",
    padding: "9px 32px 9px 30px",
    border: "1px solid #cbd5e1",
    borderRadius: "7px",
    outline: "none",
    fontSize: "11px",
    color: "#334155",
    background: "#fff",
  },

  clearButton: {
    position: "absolute",
    right: "7px",
    border: "none",
    background: "transparent",
    color: "#94a3b8",
    fontSize: "17px",
    cursor: "pointer",
    lineHeight: "1",
  },

  noData: {
    textAlign: "center",
    padding: "25px 10px",
    color: "#94a3b8",
    fontSize: "10px",
  },

  // ==========================================================
  // EMPTY
  // ==========================================================

  emptyBox: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    minHeight: "300px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 3px 10px rgba(15,23,42,0.04)",
  },

  emptyIcon: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    background: "#eff6ff",
    color: "#2563eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "22px",
    marginBottom: "12px",
  },

  emptyTitle: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#334155",
  },

  emptyText: {
    marginTop: "5px",
    fontSize: "11px",
    color: "#94a3b8",
  },

  // ==========================================================
  // BOTTOM ACTION
  // ==========================================================

  bottomAction: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "22px",
    paddingTop: "16px",
    borderTop: "1px solid #e2e8f0",
  },

  createFloorButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "9px",
    minWidth: "190px",
    padding: "10px 17px",
    border: "none",
    borderRadius: "8px",
    background: "linear-gradient(135deg, #1e3a8a, #2563eb)",
    color: "#fff",
    fontSize: "12px",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 5px 14px rgba(30,58,138,0.18)",
    transition: "all 0.2s ease",
  },

  buttonArrow: {
    fontSize: "16px",
    lineHeight: "1",
  },
};
